import React, { useCallback, useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { ThirdPartyApprovedCompaniesParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import { BaseInscription } from '../../../typings/third-party-approved-companies';
import { InscriptionStatus, IsUsedAsCloudProvider, thirdPartyApprovedCompaniesService } from '../../../services/central-api/third-party-approved-companies';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import DeleteInscriptionModal from './DeleteInscriptionModal';
import { generateInscriptionCsv, getInscriptionStatus } from '../InscriptionsCommons';
import WarningModal from '../../../components/WarningModal';
import Filter from './Filters';
import InscriptionPublishConfirmModal from './InscriptionPublishConfirmModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

export default function Inscriptions({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<ThirdPartyApprovedCompaniesParamList, 'InscriptionsManagement'>>();

    const [filters, setFilters] = useFilters<ThirdPartyApprovedCompaniesParamList['InscriptionsManagement']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);
    const [inscriptions, setInscriptions] = useState<BaseInscription[]>([]);

    const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
    const [selectedTableRows, setSelectedTableRows] = useState<BaseInscription[]>([]);
    const [currentAction, setCurrentAction] = useState<'none' | 'exportCsv' | 'confirmPublication' | 'deleteInscription'>('none');
    const [performingActionForRow, setPerformingActionForRow] = useState<string[]>([]);
    const [isLoadingActions, setIsLoadingActions] = useState<boolean>(false);

    const [selectedInscription, setSelectedInscription] = useState<BaseInscription | undefined>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [isMultipleDeleteModalVisible, setIsMultipleDeleteModalVisible] = useState<boolean>(false);

    const [isPublishUnpublishModalVisible, setIsPublishUnpublishModalVisible] = useState<boolean>(false);
    const [publishMode, setPublishMode] = useState<'publish' | 'unpublish'>('publish');

    const queryResult = useQuery({
        queryKey: ['inscriptionsTable', filters],
        queryFn: () => thirdPartyApprovedCompaniesService.getInscriptions(filters!),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setInscriptions(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setInscriptions([]);
            setTotalItems(0);
            refetch();
        }
    }, [isFocused, filters]));

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getActions() {
        const actions: Action[] = [{
            content: translate('exportCSV'),
            onPress: () => {
                setClearSelectedRows(false);
                setCurrentAction('exportCsv');
            }
        }, {
            content: translate('confirmPublication'),
            onPress: () => {
                setClearSelectedRows(false);
                setCurrentAction('confirmPublication');
            }
        }, {
            content: translate('deleteInscription'),
            variant: 'danger',
            onPress: () => {
                setClearSelectedRows(false);
                setCurrentAction('deleteInscription');
            }
        }];

        return actions;
    }

    function getSelectionTooltip() {
        if (currentAction === 'confirmPublication') {
            return <View style={{ width: 198 }}>
                <MyAppText style={{ fontSize: 12, fontWeight: '500', color: theme.colors.white['500'] }}>{translate('actionAvailableOnlyToApprovedInscription')}</MyAppText>
            </View>;
        }
        return '';
    }

    async function updateInscriptionStatus(inscriptionId: string, newStatus: InscriptionStatus) {
        try {
            const formData = new FormData();
            formData.append('status', String(newStatus));
            const updatedInscription = await thirdPartyApprovedCompaniesService.updateInscription(inscriptionId, formData);

            if (selectedInscription) {
                const infoChanged = selectedInscription.status != updatedInscription.status;
                if (!infoChanged) {
                    Toast.show({
                        type: 'sentinelxWarning',
                        text1: translate('checkIfFilesAndPublicCallAreOk'),
                    });
                    return;
                }
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setPerformingActionForRow(performingActionForRow.filter(id => id != inscriptionId));
        }
    }

    async function deleteSelectedInscriptions() {
        try {
            setIsLoadingActions(true);
            const deletedItens = await thirdPartyApprovedCompaniesService.deleteInscriptions(selectedTableRows.map(item => item.id));

            if (deletedItens.length === selectedTableRows.length) {
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
            } else {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate('someInscriptionsWereNotDeleted'),
                });
            }
        } catch (err) {
            if (err instanceof IsUsedAsCloudProvider) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('isUsedAsCloudProvider'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSelectedTableRows([]);
            setIsLoadingActions(false);
            setCurrentAction('none');
            refetch();
        }
    }

    async function exportMultipleInscription() {
        try {
            setIsLoadingActions(true);
            await generateInscriptionCsv(selectedTableRows.map(r => r.id));
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSelectedTableRows([]);
            setIsLoadingActions(false);
            setCurrentAction('none');
        }
    }

    async function updateMultipleInscriptions() {
        try {
            setIsLoadingActions(true);
            const updatedInscriptions = await thirdPartyApprovedCompaniesService.updateMultipleInscriptions({
                ids: selectedTableRows.map(item => item.id),
                status: 'published'
            });

            const allInscriptionsUpdated = updatedInscriptions.every(i => i.status === 'published');

            if (allInscriptionsUpdated) {
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
            } else {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate('someInscriptionsWereNotUpdated'),
                });
            }
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSelectedTableRows([]);
            setIsLoadingActions(false);
            setCurrentAction('none');
            refetch();
        }
    }

    return (
        <Container>
            <Header
                title={translate('inscriptionsManagement')}
                actions={currentAction === 'none'
                    ? <CustomButton
                        text={translate('registerCompany')}
                        onPress={() => {
                            setSelectedInscription(undefined);
                            navigation.navigate('InscriptionForm');
                        }}
                    />
                    : null}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredInscriptions')}
                    zIndex={2}
                    filter={
                        <View style={styles.buttonsContainer}>
                            <Filter
                                filters={filters}
                                setFilters={setFilters}
                                textFilter={textFilter}
                                setTextFilter={setTextFilter}
                            />
                            {currentAction === 'none'
                                ? <DropdownButton
                                    isLoading={isLoadingActions}
                                    viewStyle={{ height: '100%' }}
                                    isButton
                                    actions={getActions()}
                                    buttonIcon={faChevronDown}
                                    buttonType='primary'
                                    buttonLabel='actions'
                                    offsetX={39}
                                    offsetY={45}
                                    size={164}
                                />
                                : <>
                                    <CustomButton
                                        text={translate('cancelSelection')}
                                        type='secondary'
                                        onPress={() => {
                                            setClearSelectedRows(true);
                                            setSelectedTableRows([]);
                                            setCurrentAction('none');
                                        }}
                                        isLoading={isLoadingActions}
                                    />
                                    <CustomButton
                                        text={translate('concludeSelection')}
                                        type='primary'
                                        disabled={selectedTableRows.length === 0}
                                        onPress={() => {
                                            if (currentAction === 'deleteInscription') {
                                                setIsMultipleDeleteModalVisible(true);
                                            } else if (currentAction === 'exportCsv') {
                                                setClearSelectedRows(true);
                                                exportMultipleInscription();
                                            } else if (currentAction === 'confirmPublication') {
                                                setClearSelectedRows(true);
                                                updateMultipleInscriptions();
                                            }
                                        }}
                                        isLoading={isLoadingActions}
                                    />
                                </>
                            }
                        </View>
                    }
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        clearSelectedRows={clearSelectedRows}
                        selectableRows={currentAction !== 'none'}
                        disableSelectTooltip={getSelectionTooltip()}
                        selectableRowDisabled={(row) => {
                            if (currentAction === 'exportCsv' || currentAction === 'deleteInscription') {
                                return false;
                            } else if (currentAction === 'confirmPublication') {
                                return row.status !== 'approved';
                            } else {
                                return true;
                            }
                        }}
                        onSelectedRowsChange={(selectedRows) => {
                            setSelectedTableRows(selectedRows.selectedRows);
                        }}
                        columns={[
                            {
                                name: translate('id'),
                                cell: row => row.id,
                            },
                            {
                                name: translate('status'),
                                cell: row => getInscriptionStatus({ status: row.status }),
                                grow: 0,
                            },
                            {
                                name: translate('inscriptionType'),
                                cell: row => row.companyType === 'both' ? `${translate('cloudInscriptionType')}, ${translate('imagesInscriptionType')}` : translate(`${row.companyType}InscriptionType`),
                                grow: 1,
                            },
                            {
                                name: translate('companyName'),
                                cell: row => row.companyName,
                                grow: 1,
                            },
                            {
                                name: translate('tradeName'),
                                cell: row => row.tradeName,
                                grow: 1,
                            },
                            {
                                name: translate('cnpj'),
                                cell: row => row.cnpj.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'),
                                grow: 1,
                            },
                            {
                                name: translate('inscriptionResponsibleName'),
                                cell: row => row.name,
                                grow: 1,
                            },
                            {
                                name: translate('inscriptionResponsiblePhone'),
                                cell: row => row.phone.replace(/\D/g, '').replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3'),
                                grow: 1,
                            },
                            {
                                name: translate('enrolledIn'),
                                cell: row => moment(row.createdAt).format('DD/MM/YYYY'),
                                grow: 1,
                            },
                            {
                                name: '',
                                button: true,
                                cell: row => {
                                    const actions: Action[] = [];

                                    actions.push({
                                        content: translate('exportCSV'),
                                        onPress: async () => {
                                            try {
                                                setPerformingActionForRow([
                                                    ...performingActionForRow,
                                                    row.id
                                                ]);
                                                await generateInscriptionCsv([row.id]);
                                            } finally {
                                                setPerformingActionForRow(performingActionForRow.filter(id => id != row.id));
                                            }
                                        }
                                    });

                                    actions.push({
                                        content: !['published', 'failed'].includes(row.status) ? translate('editInformation') : translate('viewInformation'),
                                        onPress: () => {
                                            setSelectedInscription(row);
                                            navigation.navigate('InscriptionForm', { id: row.id });
                                        },
                                    });

                                    if (row.status === 'approved') {
                                        actions.push({
                                            content: translate('confirmPublication'),
                                            onPress: () => {
                                                setSelectedInscription(row);
                                                setPublishMode('publish');
                                                setIsPublishUnpublishModalVisible(true);
                                            }
                                        });
                                    }

                                    if (row.status === 'published') {
                                        actions.push({
                                            content: translate('cancelPublication'),
                                            onPress: () => {
                                                setSelectedInscription(row);
                                                setPublishMode('unpublish');
                                                setIsPublishUnpublishModalVisible(true);
                                            }
                                        });
                                    }

                                    actions.push({
                                        content: translate('deleteInscription'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setSelectedInscription(row);
                                            setIsDeleteModalVisible(true);
                                        }
                                    });

                                    return (
                                        <DropdownButton
                                            size={180}
                                            actions={actions}
                                            isLoading={performingActionForRow.includes(row.id)}
                                        />
                                    );
                                }
                            }
                        ]}
                        data={inscriptions}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>

            {selectedInscription && <InscriptionPublishConfirmModal
                isModalVisible={isPublishUnpublishModalVisible}
                setModalVisible={setIsPublishUnpublishModalVisible}
                mode={publishMode}
                onConfirm={async (newStatus) => {
                    setPerformingActionForRow([
                        ...performingActionForRow,
                        selectedInscription.id
                    ]);
                    await updateInscriptionStatus(selectedInscription.id, newStatus);
                }}
            />}
            {selectedInscription && <DeleteInscriptionModal
                onConfirm={refetch}
                selectedInscription={selectedInscription}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}

            <WarningModal
                isModalVisible={isMultipleDeleteModalVisible}
                setIsModalVisible={setIsMultipleDeleteModalVisible}
                text={translate('thisActionCannotBeUndone')}
                items={selectedTableRows.map(item => item.companyName)}
                onClose={() => setIsMultipleDeleteModalVisible(false)}
                onSubmit={async () => {
                    setClearSelectedRows(true);
                    setIsMultipleDeleteModalVisible(false);
                    await deleteSelectedInscriptions();
                }}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet(() => ({
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
}));
